import React, { useEffect, useRef } from "react";
import styled from "@emotion/styled/macro";

// Components
import { Modal } from "../common/modal";
import { Image } from "../ui/image";
import { Color, FontSize } from "../styles/enums";
import { rubyStyles } from "../styles/mixins/ruby";
import { fromMarkdown, toMarkdown } from "../../models/episode";
import { parseRubyText } from "../../utils/ruby-parser";
import hrDivider from "../../assets/editor/horizontal_divider.svg";
import closeIcon from "../../assets/header/close_icon.svg";

const ContentWrapper = styled.div`
  background-color: ${Color.PRIMARY};
  height: 100svh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
`;

const Header = styled.div`
  position: sticky;
  top: 0;
  background-color: ${Color.PRIMARY};
  padding: 15px;
  border-bottom: 1px solid ${Color.ACCENT_50};
  font-size: ${FontSize.SIZE_16};
  font-weight: 700;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseButton = styled.button`
  position: absolute;
  left: 16px;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

const Title = styled.span`
  text-align: center;
`;

const Content = styled.div`
  padding: 20px;
  padding-bottom: 55vh;
`;

const EpisodeContent = styled.div`
  font-family:
    helvetica, "Hiragino Sans", "Hiragino Kaku Gothic ProN",
    "ヒラギノ角ゴ ProN W3", sans-serif;
  font-size: ${FontSize.SIZE_16};
  line-height: 1.8;
  overflow-wrap: break-word;

  em {
    font-family: serif;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0.67em 0;
  }

  img {
    max-width: 100%;
    max-height: 100vh;
    display: block;
    margin-left: auto;
    margin-right: auto;
  }

  hr {
    height: 0;
    padding: 0 20px;
    margin: 10px 0 40px 0;

    border: 0;
    text-align: center;

    &:before {
      content: url(${hrDivider});
      position: relative;
      top: -20px;
    }
  }

  ${rubyStyles}
`;

interface Props {
  open: boolean;
  content: string;
  title?: string;
  onClose(): void;
}

export const EpisodePreviewModal: React.FC<Props> = ({
  open,
  content,
  title,
  onClose,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && contentRef.current && content) {
      const markdown = toMarkdown(content);
      const html = fromMarkdown(markdown, { forQuill: false });
      contentRef.current.innerHTML = parseRubyText(html);
    }
  }, [open, content]);

  if (!open) {
    return null;
  }

  return (
    <Modal
      open={open}
      width="100vw"
      height="100svh"
      maxWidth="100vw"
      borderRadius="0"
      hideClose
      close={onClose}
      noPadding
      scrollableElement={scrollableRef}
    >
      <ContentWrapper ref={scrollableRef}>
        <Header>
          <CloseButton type="button" onClick={onClose}>
            <Image src={closeIcon} />
          </CloseButton>
          <Title>{title ? `${title} ー プレビュー` : "プレビュー"}</Title>
        </Header>
        <Content>
          <EpisodeContent ref={contentRef} />
        </Content>
      </ContentWrapper>
    </Modal>
  );
};
